import type { ReactNode } from "react";
import {
  AlertTriangle,
  BarChart3,
  FolderKanban,
  PieChart,
  RotateCcw,
  TimerReset,
  Tickets,
  TrendingUp,
  UserRound,
} from "lucide-react";
import {
  formatItTime,
  getClosedReopenCount,
  getClosedReopenDistinctOriginCount,
  getIncidentBaseCode,
  getItStateLabel,
  getRejectedCorrectionCount,
  getRejectedCorrectionDistinctIncidentCount,
  itStateOrder,
  type ItIncident,
  type ItPrioridad,
} from "../types/it";

type Props = {
  incidents: ItIncident[];
  allIncidents: ItIncident[];
};

type ChartRow = {
  label: string;
  value: number;
  percentage: number;
  tone?: string;
};

type PanelTone = "green" | "slate" | "amber" | "rose" | "teal";

export function ItDashboardPanel({ incidents, allIncidents }: Props) {
  const total = incidents.length;

  const active = incidents.filter((item) =>
    ["aprobada", "creada", "iniciada", "corregida"].includes(item.estado),
  ).length;

  const closed = incidents.filter((item) => item.estado === "cerrada").length;

  const totalMinutes = incidents.reduce((carry, item) => {
    if (!item.tiempo_dedicado) return carry;

    const [hours = "0", minutes = "0"] = item.tiempo_dedicado.split(":");
    return carry + Number(hours) * 60 + Number(minutes);
  }, 0);

  const totalClosedReopens = getClosedReopenCount(allIncidents);
  const totalClosedReopenedOrigins =
    getClosedReopenDistinctOriginCount(allIncidents);
  const totalRejectedCorrections = getRejectedCorrectionCount(allIncidents);
  const totalRejectedCorrectionIncidents =
    getRejectedCorrectionDistinctIncidentCount(allIncidents);

  const stateRows = buildStateRows(incidents, total);
  const priorityRows = buildPriorityRows(incidents, total);
  const projectRows = buildAggregateRows(
    incidents,
    (item) => item.proyecto_nombre || "Sin proyecto",
    total,
  );
  const categoryRows = buildAggregateRows(
    incidents,
    (item) => item.categoria || "Sin categoría",
    total,
  );
  const responsibleRows = buildAggregateRows(
    incidents.filter((item) => item.responsable_id !== null),
    (item) => item.responsable_nombre || "Sin asignar",
    total,
  );
  const reopenedRows = buildReopenedRows(allIncidents);

  return (
    <div className="scroll-reveal-up flex flex-col gap-5">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Total incidencias"
          value={String(total)}
          subtitle="según filtros activos"
          icon={Tickets}
          tone="green"
          index={0}
        />

        <MetricCard
          title="Activas"
          value={String(active)}
          subtitle="en flujo operativo"
          icon={TimerReset}
          tone="teal"
          index={1}
        />

        <MetricCard
          title="Cerradas"
          value={String(closed)}
          subtitle="visibles y validadas"
          icon={RotateCcw}
          tone="slate"
          index={2}
        />

        <MetricCard
          title="Tiempo total"
          value={formatItTime(
            `${Math.floor(totalMinutes / 60)
              .toString()
              .padStart(2, "0")}:${(totalMinutes % 60)
              .toString()
              .padStart(2, "0")}:00`,
          )}
          subtitle="dedicación registrada"
          icon={AlertTriangle}
          tone="amber"
          index={3}
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
        <PanelCard hover="hover:border-[#00d09c]/22">
          <PanelHeader
            icon={BarChart3}
            eyebrow="Workflow"
            title="Distribución por estado"
            description="Peso real del flujo operativo actual."
            tone="green"
          />

          <div className="bg-white/70 p-5">
            {stateRows.length === 0 ? (
              <EmptyCopy text="No hay estados con incidencias en el filtro actual." />
            ) : (
              <StateBarChart rows={stateRows} />
            )}
          </div>
        </PanelCard>

        <PanelCard delay="dashboard-delay-1" hover="hover:border-[#00d09c]/22">
          <PanelHeader
            icon={PieChart}
            eyebrow="Prioridades"
            title="Distribución por prioridad"
            description="Alta, media y baja con peso porcentual y cantidad."
            tone="slate"
          />

          <div className="grid gap-6 bg-white/70 p-5 lg:grid-cols-[280px_minmax(0,1fr)]">
            <PriorityDonut rows={priorityRows} total={total} />

            <div className="space-y-3">
              {priorityRows.map((row) => (
                <PriorityLegendRow
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  percentage={row.percentage}
                  tone={row.tone || "#0b4a2f"}
                />
              ))}
            </div>
          </div>
        </PanelCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <PanelCard delay="dashboard-delay-2" hover="hover:border-[#00d09c]/22">
          <PanelHeader
            icon={FolderKanban}
            eyebrow="Carga"
            title="Proyectos más cargados"
            description="Distribución visual del volumen de incidencias por proyecto."
            tone="green"
          />

          <ScrollableStatList
            rows={projectRows}
            emptyText="No hay proyectos relacionados en el filtro actual."
            tone="from-[#0b4a2f] via-[#0f5b3b] to-[#00a879]"
          />
        </PanelCard>

        <PanelCard delay="dashboard-delay-3" hover="hover:border-[#00d09c]/22">
          <PanelHeader
            icon={TrendingUp}
            eyebrow="Categorías"
            title="Categorías más cargadas"
            description="Volumen operativo agrupado por categoría de incidencia."
            tone="teal"
          />

          <ScrollableStatList
            rows={categoryRows}
            emptyText="No hay categorías relacionadas en el filtro actual."
            tone="from-slate-800 via-[#0b4a2f] to-[#00a879]"
          />
        </PanelCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <PanelCard delay="dashboard-delay-4" hover="hover:border-[#00d09c]/22">
          <PanelHeader
            icon={UserRound}
            eyebrow="Responsables"
            title="Carga por técnico"
            description="Cantidad y porcentaje de incidencias asignadas a cada responsable."
            tone="green"
          />

          <ScrollableStatList
            rows={responsibleRows}
            emptyText="No hay incidencias asignadas en el filtro actual."
            tone="from-[#0b4a2f] via-slate-700 to-slate-500"
          />
        </PanelCard>

        <PanelCard delay="dashboard-delay-5" hover="hover:border-rose-200">
          <PanelHeader
            icon={RotateCcw}
            eyebrow="Fricción"
            title="Incidencias más reabiertas"
            description="La incidencia origen y sus veces de reapertura tras cierre."
            tone="rose"
          />

          <ScrollableStatList
            rows={reopenedRows}
            emptyText="Todavía no hay reaperturas tras cierre en el filtro actual."
            tone="from-rose-500 via-amber-500 to-[#0b4a2f]"
          />
        </PanelCard>
      </div>

      <PanelCard delay="dashboard-delay-6" hover="hover:border-amber-200">
        <PanelHeader
          icon={TrendingUp}
          eyebrow="Reaperturas"
          title="Indicadores de reapertura y rechazo"
          description="Separación entre incidencias afectadas y número total de veces."
          tone="amber"
        />

        <div className="grid gap-4 bg-white/70 p-5 md:grid-cols-2 xl:grid-cols-4">
          <QualityCard
            label='Reabiertas tras cierre "Cantidad de incidencias"'
            value={String(totalClosedReopenedOrigins)}
            help="Incidencias origen que se reabrieron al menos una vez tras cierre."
            tone="green"
          />

          <QualityCard
            label='Reabiertas tras cierre "Cantidad de veces"'
            value={String(totalClosedReopens)}
            help="Número total de reaperturas creadas desde incidencias cerradas."
            tone="slate"
          />

          <QualityCard
            label='Rechazos de corrección "Cantidad de incidencias"'
            value={String(totalRejectedCorrectionIncidents)}
            help="Incidencias que sufrieron al menos un rechazo de corrección."
            tone="amber"
          />

          <QualityCard
            label='Rechazos de corrección "Cantidad de veces"'
            value={String(totalRejectedCorrections)}
            help="Número total de veces que una corrección fue rechazada."
            tone="rose"
          />
        </div>
      </PanelCard>
    </div>
  );
}

function buildStateRows(incidents: ItIncident[], total: number): ChartRow[] {
  return itStateOrder
    .map((estado) => {
      const value = incidents.filter((item) => item.estado === estado).length;

      return {
        label: getItStateLabel(estado),
        value,
        percentage: total > 0 ? Math.round((value / total) * 100) : 0,
      };
    })
    .filter((row) => row.value > 0);
}

function buildPriorityRows(incidents: ItIncident[], total: number): ChartRow[] {
  const tones: Record<ItPrioridad, string> = {
    alta: "#e11d48",
    media: "#f59e0b",
    baja: "#0b4a2f",
  };

  return (["alta", "media", "baja"] as ItPrioridad[]).map((priority) => {
    const value = incidents.filter(
      (item) => item.prioridad === priority,
    ).length;

    return {
      label: priority[0]?.toUpperCase() + priority.slice(1),
      value,
      percentage: total > 0 ? Math.round((value / total) * 100) : 0,
      tone: tones[priority],
    };
  });
}

function buildAggregateRows(
  incidents: ItIncident[],
  getLabel: (incident: ItIncident) => string,
  total: number,
): ChartRow[] {
  return Object.values(
    incidents.reduce<Record<string, ChartRow>>((carry, incident) => {
      const label = getLabel(incident);
      carry[label] = carry[label] ?? { label, value: 0, percentage: 0 };
      carry[label].value += 1;
      return carry;
    }, {}),
  )
    .map((row) => ({
      ...row,
      percentage: total > 0 ? Math.round((row.value / total) * 100) : 0,
    }))
    .sort((left, right) => right.value - left.value);
}

function buildReopenedRows(allIncidents: ItIncident[]): ChartRow[] {
  const totalReopens = getClosedReopenCount(allIncidents);

  return Object.values(
    allIncidents.reduce<Record<string, ChartRow>>((carry, incident) => {
      if (incident.incidencia_origen_id === null) {
        return carry;
      }

      const label = getIncidentBaseCode(incident.codigo);
      carry[label] = carry[label] ?? { label, value: 0, percentage: 0 };
      carry[label].value += 1;
      return carry;
    }, {}),
  )
    .map((row) => ({
      ...row,
      percentage:
        totalReopens > 0 ? Math.round((row.value / totalReopens) * 100) : 0,
    }))
    .sort((left, right) => right.value - left.value);
}

function PanelCard({
  children,
  delay = "",
  hover,
}: {
  children: ReactNode;
  delay?: string;
  hover: string;
}) {
  return (
    <section
      className={[
        "dashboard-scale-in dashboard-component-shell overflow-hidden rounded-[34px] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_34px_90px_rgba(15,23,42,.14)]",
        delay,
        hover,
      ].join(" ")}
    >
      {children}
    </section>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  tone,
  index,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: typeof Tickets;
  tone: PanelTone;
  index: number;
}) {
  const palette = metricTone[tone];

  return (
    <section
      className="dashboard-scale-in group relative overflow-hidden rounded-[32px] border border-[#dfe8e4] bg-white p-5 shadow-[0_24px_65px_rgba(15,23,42,.10)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:border-[#00d09c]/22 hover:shadow-[0_34px_85px_rgba(15,23,42,.15)]"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className={`absolute inset-0 ${palette.surface}`} />

      <div
        className={[
          "absolute right-[-64px] top-[-76px] h-48 w-48 rounded-full blur-3xl transition duration-500 group-hover:scale-125 group-hover:opacity-100",
          palette.orb,
        ].join(" ")}
      />

      <div
        className={`absolute inset-x-6 top-0 h-px bg-gradient-to-r ${palette.line}`}
      />

      <div className="relative z-10 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
            {title}
          </div>

          <div className="mt-4 text-4xl font-black tracking-tight text-slate-950">
            {value}
          </div>

          <div className="mt-2 text-sm font-semibold leading-6 text-slate-500">
            {subtitle}
          </div>
        </div>

        <span
          className={[
            "grid h-12 w-12 place-items-center rounded-2xl border shadow-[0_14px_30px_rgba(15,23,42,.075)] backdrop-blur-xl transition-all duration-300 group-hover:-rotate-6 group-hover:scale-110",
            palette.icon,
          ].join(" ")}
        >
          <Icon size={19} />
        </span>
      </div>

      <div className="relative z-10 mt-5 h-2 overflow-hidden rounded-full bg-slate-100 shadow-inner">
        <div
          className={[
            "h-full w-2/3 rounded-full bg-gradient-to-r transition-all duration-700 group-hover:w-full",
            palette.bar,
          ].join(" ")}
        />
      </div>
    </section>
  );
}

function PanelHeader({
  icon: Icon,
  eyebrow,
  title,
  description,
  tone,
}: {
  icon: typeof BarChart3;
  eyebrow: string;
  title: string;
  description: string;
  tone: PanelTone;
}) {
  const palette = panelTone[tone];

  return (
    <header
      className={`relative overflow-hidden border-b px-6 py-5 ${palette.header}`}
    >
      <div
        className={[
          "pointer-events-none absolute -right-20 top-[-90px] h-[240px] w-[240px] rounded-full blur-3xl",
          palette.orb,
        ].join(" ")}
      />

      <div className="relative flex items-center gap-4">
        <span
          className={[
            "grid h-13 w-13 place-items-center rounded-[24px] border bg-white shadow-[0_14px_32px_rgba(15,23,42,.10)] backdrop-blur-xl transition-all duration-300 hover:-rotate-6 hover:scale-110",
            palette.icon,
          ].join(" ")}
        >
          <Icon size={22} />
        </span>

        <div>
          <div
            className={`text-xs font-black uppercase tracking-[0.18em] ${palette.eyebrow}`}
          >
            {eyebrow}
          </div>

          <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">
            {title}
          </h3>

          <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
            {description}
          </p>
        </div>
      </div>
    </header>
  );
}

function StateBarChart({ rows }: { rows: ChartRow[] }) {
  const maxValue = Math.max(...rows.map((row) => row.value), 1);
  const tones = [
    "from-[#0b4a2f] via-[#0f5b3b] to-[#00a879]",
    "from-slate-800 via-[#0b4a2f] to-[#00d09c]",
    "from-slate-700 via-slate-600 to-[#00a879]",
    "from-amber-500 via-orange-400 to-[#0b4a2f]",
    "from-rose-500 via-amber-500 to-[#0b4a2f]",
    "from-slate-900 via-slate-700 to-slate-500",
  ];

  return (
    <div className="rounded-[28px] border border-[#dfe8e4] bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,.055)]">
      <div className="flex min-h-[280px] items-end gap-3 overflow-x-auto pb-2">
        {rows.map((row, index) => {
          const height = Math.max((row.value / maxValue) * 100, 10);
          const tone = tones[index % tones.length];

          return (
            <div
              key={row.label}
              className="group flex min-w-[88px] flex-1 flex-col items-center justify-end gap-3"
            >
              <div className="text-center">
                <div className="text-sm font-black text-slate-950">
                  {row.value}
                </div>
                <div className="text-[11px] font-black text-slate-400">
                  {row.percentage}%
                </div>
              </div>

              <div className="flex h-44 w-full items-end rounded-2xl border border-[#dfe8e4] bg-[linear-gradient(180deg,#f8fafc,#f1f5f9)] p-1.5 shadow-inner">
                <div
                  className={`w-full rounded-xl bg-gradient-to-t ${tone} shadow-[0_14px_28px_rgba(11,74,47,.16)] transition-all duration-700 group-hover:brightness-110`}
                  style={{ height: `${height}%` }}
                />
              </div>

              <div className="line-clamp-2 min-h-[34px] text-center text-xs font-black leading-4 text-slate-600">
                {row.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScrollableStatList({
  rows,
  emptyText,
  tone,
}: {
  rows: ChartRow[];
  emptyText: string;
  tone: string;
}) {
  const shouldScroll = rows.length > 4;

  return (
    <div className="bg-white/70 p-5">
      {rows.length === 0 ? (
        <EmptyCopy text={emptyText} />
      ) : (
        <div
          className={[
            "space-y-3 pr-1",
            shouldScroll
              ? "max-h-[372px] overflow-y-auto overscroll-contain [scrollbar-color:rgba(15,23,42,.16)_transparent] [scrollbar-width:thin]"
              : "",
          ].join(" ")}
        >
          {rows.map((row) => (
            <BarStatRow
              key={row.label}
              label={row.label}
              value={row.value}
              percentage={row.percentage}
              tone={tone}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PriorityDonut({ rows, total }: { rows: ChartRow[]; total: number }) {
  const gradient = rows
    .reduce<{ parts: string[]; offset: number }>(
      (carry, row) => {
        const nextOffset = carry.offset + row.percentage;
        carry.parts.push(`${row.tone} ${carry.offset}% ${nextOffset}%`);
        return { parts: carry.parts, offset: nextOffset };
      },
      { parts: [], offset: 0 },
    )
    .parts.join(", ");

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div
        className="relative h-56 w-56 rounded-full shadow-[0_24px_60px_rgba(15,23,42,.12)]"
        style={{
          background:
            total > 0
              ? `conic-gradient(${gradient})`
              : "conic-gradient(#e2e8f0 0% 100%)",
        }}
      >
        <div className="absolute inset-[24px] grid place-items-center rounded-full border border-white/80 bg-white shadow-inner">
          <div className="text-center">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
              Prioridades
            </div>
            <div className="mt-2 text-4xl font-black text-slate-950">
              {total}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-500">
              incidencias
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PriorityLegendRow({
  label,
  value,
  percentage,
  tone,
}: {
  label: string;
  value: number;
  percentage: number;
  tone: string;
}) {
  return (
    <div className="rounded-[24px] border border-[#dfe8e4] bg-white p-4 shadow-[0_10px_26px_rgba(15,23,42,.045)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00d09c]/22 hover:bg-slate-50">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className="h-3.5 w-3.5 rounded-full shadow-[0_0_0_4px_rgba(148,163,184,.14)]"
            style={{ backgroundColor: tone }}
          />
          <span className="text-sm font-black text-slate-800">{label}</span>
        </div>

        <span className="rounded-full border border-[#dfe8e4] bg-white px-3 py-1 text-xs font-black text-slate-700 shadow-sm">
          {value}
        </span>
      </div>

      <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${percentage}%`, backgroundColor: tone }}
        />
      </div>

      <div className="mt-2 text-right text-xs font-bold text-slate-400">
        {percentage}%
      </div>
    </div>
  );
}

function BarStatRow({
  label,
  value,
  percentage,
  tone,
}: {
  label: string;
  value: number;
  percentage: number;
  tone: string;
}) {
  return (
    <div className="rounded-[24px] border border-[#dfe8e4] bg-white p-4 shadow-[0_10px_26px_rgba(15,23,42,.045)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00d09c]/22 hover:bg-slate-50">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="truncate text-sm font-black text-slate-800">
          {label}
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded-full border border-[#dfe8e4] bg-white px-3 py-1 text-xs font-black text-slate-700 shadow-sm">
            {value}
          </span>
          <span className="text-xs font-black text-slate-400">
            {percentage}%
          </span>
        </div>
      </div>

      <div className="h-3 overflow-hidden rounded-full bg-slate-100 shadow-inner">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${tone} shadow-[0_8px_20px_rgba(11,74,47,.14)] transition-all duration-700`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function QualityCard({
  label,
  value,
  help,
  tone,
}: {
  label: string;
  value: string;
  help: string;
  tone: "green" | "slate" | "amber" | "rose";
}) {
  const palette = qualityTone[tone];

  return (
    <div
      className={[
        "group relative overflow-hidden rounded-[28px] border p-5 shadow-[0_14px_34px_rgba(15,23,42,.06)] transition-all duration-300 hover:-translate-y-1",
        palette.container,
      ].join(" ")}
    >
      <div
        className={[
          "pointer-events-none absolute -right-12 -top-14 h-36 w-36 rounded-full blur-3xl transition duration-500 group-hover:scale-125",
          palette.orb,
        ].join(" ")}
      />

      <div
        className={`relative text-xs font-black uppercase tracking-[0.16em] ${palette.label}`}
      >
        {label}
      </div>

      <div className={`relative mt-3 text-4xl font-black ${palette.value}`}>
        {value}
      </div>

      <div className="relative mt-3 text-sm font-semibold leading-6 text-slate-500">
        {help}
      </div>
    </div>
  );
}

function EmptyCopy({ text }: { text: string }) {
  return (
    <div className="rounded-[24px] border border-dashed border-[#dfe8e4] bg-slate-50/80 p-6 text-center text-sm font-bold leading-6 text-slate-400">
      {text}
    </div>
  );
}

const metricTone = {
  green: {
    surface:
      "bg-[linear-gradient(135deg,rgba(255,255,255,.98),rgba(247,251,248,.90)_52%,rgba(0,208,156,.08))]",
    orb: "bg-[#00d09c]/18",
    line: "from-transparent via-[#00d09c]/60 to-transparent",
    icon: "border-[#00d09c]/18 bg-[#00d09c]/9 text-[#0b4a2f]",
    bar: "from-[#0b4a2f] via-[#0f5b3b] to-[#00a879]",
  },
  teal: {
    surface:
      "bg-[linear-gradient(135deg,rgba(255,255,255,.98),rgba(247,251,248,.90)_52%,rgba(0,195,175,.08))]",
    orb: "bg-[#00c3af]/16",
    line: "from-transparent via-[#00c3af]/45 to-transparent",
    icon: "border-[#00c3af]/16 bg-[#00c3af]/8 text-[#0b4a2f]",
    bar: "from-[#0b4a2f] via-[#00a879] to-[#00c3af]",
  },
  slate: {
    surface:
      "bg-[linear-gradient(135deg,rgba(255,255,255,.98),rgba(248,250,252,.90)_52%,rgba(226,232,240,.72))]",
    orb: "bg-slate-300/24",
    line: "from-transparent via-slate-300/75 to-transparent",
    icon: "border-slate-200 bg-slate-50 text-slate-700",
    bar: "from-slate-900 via-slate-700 to-slate-500",
  },
  amber: {
    surface:
      "bg-[linear-gradient(135deg,rgba(255,255,255,.98),rgba(255,251,235,.90)_52%,rgba(254,243,199,.62))]",
    orb: "bg-amber-300/22",
    line: "from-transparent via-amber-300/75 to-transparent",
    icon: "border-amber-100 bg-amber-50 text-amber-700",
    bar: "from-amber-400 via-orange-400 to-[#0b4a2f]",
  },
  rose: {
    surface:
      "bg-[linear-gradient(135deg,rgba(255,255,255,.98),rgba(255,241,242,.90)_52%,rgba(252,231,243,.60))]",
    orb: "bg-rose-300/22",
    line: "from-transparent via-rose-300/75 to-transparent",
    icon: "border-rose-100 bg-rose-50 text-rose-700",
    bar: "from-rose-500 via-pink-500 to-[#0b4a2f]",
  },
};

const panelTone = {
  green: {
    header:
      "border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_54%,#eef8f3_100%)]",
    orb: "bg-[#00d09c]/12",
    icon: "border-[#00d09c]/18 text-[#0b4a2f]",
    eyebrow: "text-[#0b4a2f]",
  },
  teal: {
    header:
      "border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_54%,#ecfdf8_100%)]",
    orb: "bg-[#00c3af]/12",
    icon: "border-[#00c3af]/18 text-[#0b4a2f]",
    eyebrow: "text-[#0b4a2f]",
  },
  slate: {
    header:
      "border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_54%,#f1f5f9_100%)]",
    orb: "bg-slate-300/20",
    icon: "border-slate-200 text-slate-700",
    eyebrow: "text-slate-600",
  },
  amber: {
    header:
      "border-amber-100/80 bg-[linear-gradient(135deg,#ffffff_0%,#fffbeb_54%,#fff7ed_100%)]",
    orb: "bg-amber-300/22",
    icon: "border-amber-100 text-amber-700",
    eyebrow: "text-amber-700",
  },
  rose: {
    header:
      "border-rose-100/80 bg-[linear-gradient(135deg,#ffffff_0%,#fff1f2_54%,#fdf2f8_100%)]",
    orb: "bg-rose-300/20",
    icon: "border-rose-100 text-rose-700",
    eyebrow: "text-rose-700",
  },
};

const qualityTone = {
  green: {
    container: "border-[#00d09c]/18 bg-[#00d09c]/8 hover:bg-[#00d09c]/10",
    orb: "bg-[#00d09c]/16",
    label: "text-[#0b4a2f]",
    value: "text-slate-950",
  },
  slate: {
    container: "border-slate-200 bg-slate-50/80 hover:bg-slate-50",
    orb: "bg-slate-300/22",
    label: "text-slate-600",
    value: "text-slate-950",
  },
  amber: {
    container: "border-amber-100 bg-amber-50/82 hover:bg-amber-50",
    orb: "bg-amber-300/22",
    label: "text-amber-700",
    value: "text-amber-900",
  },
  rose: {
    container: "border-rose-100 bg-rose-50/80 hover:bg-rose-50",
    orb: "bg-rose-300/20",
    label: "text-rose-700",
    value: "text-rose-900",
  },
};
